import {
  isSkillShortcutEvidence,
  type SkillShortcutEvidence,
} from './skill-shortcuts';
import {
  SESSION_BINDING_REASON_CODES,
  type SessionBindingRuntimeKind,
} from './session-binding';

export const SESSION_INTEGRATION_STATES = [
  'integrated',
  'degraded',
  'blocked',
] as const;

export type SessionIntegrationState =
  (typeof SESSION_INTEGRATION_STATES)[number];

export const SESSION_OBSERVABILITY_CAPABILITIES = [
  'binding',
  'mcp',
  'skill-vault',
  'skill-lifecycle',
  'usage',
  'finalization',
] as const;

export type SessionObservabilityCapability =
  (typeof SESSION_OBSERVABILITY_CAPABILITIES)[number];

export const SESSION_EVIDENCE_STATES = [
  'confirmed',
  'pending',
  'rejected',
  'unavailable',
  'unsupported',
] as const;

export type SessionEvidenceState = (typeof SESSION_EVIDENCE_STATES)[number];

export const SESSION_OBSERVABILITY_REASON_CODES = [
  ...SESSION_BINDING_REASON_CODES,
  'MCP_UNAVAILABLE',
  'MCP_PROTOCOL_UNSUPPORTED',
  'SKILL_VAULT_UNAVAILABLE',
  'SKILL_VAULT_UNAUTHORIZED',
  'SKILL_VAULT_EMPTY',
  'SKILL_LIFECYCLE_NOT_OBSERVED',
  'SKILL_LIFECYCLE_TERMINAL_MISSING',
  'USAGE_NOT_OBSERVED',
  'USAGE_ID_MISSING',
  'FINALIZATION_NOT_OBSERVED',
  'RUNTIME_CAPABILITY_UNSUPPORTED',
] as const;

export type SessionObservabilityReasonCode =
  (typeof SESSION_OBSERVABILITY_REASON_CODES)[number];

export const SESSION_OBSERVABILITY_ACTION_CODES = [
  'none',
  'retry',
  'reconnect',
  'repair',
  'upgrade-runtime',
] as const;

export type SessionObservabilityActionCode =
  (typeof SESSION_OBSERVABILITY_ACTION_CODES)[number];

export interface SessionCapabilityEvidence {
  capability: SessionObservabilityCapability;
  state: SessionEvidenceState;
  reasonCode: SessionObservabilityReasonCode | null;
  observedAt: string | null;
}

export interface SessionObservabilityProjection {
  skillShortcuts?: SkillShortcutEvidence;
  runtimeKind: Extract<SessionBindingRuntimeKind, 'claude' | 'codex'>;
  state: SessionIntegrationState;
  capabilities: SessionCapabilityEvidence[];
  actionCode: SessionObservabilityActionCode;
  observedAt: string;
}

const OBSERVABILITY_PROJECTION_KEYS = [
  'skillShortcuts',
  'runtimeKind',
  'state',
  'capabilities',
  'actionCode',
  'observedAt',
] as const;

const CAPABILITY_EVIDENCE_KEYS = [
  'capability',
  'state',
  'reasonCode',
  'observedAt',
] as const;

export function isSessionIntegrationState(
  value: unknown,
): value is SessionIntegrationState {
  return SESSION_INTEGRATION_STATES.some(state => state === value);
}

export function isSessionObservabilityCapability(
  value: unknown,
): value is SessionObservabilityCapability {
  return SESSION_OBSERVABILITY_CAPABILITIES.some(
    capability => capability === value,
  );
}

export function isSessionEvidenceState(
  value: unknown,
): value is SessionEvidenceState {
  return SESSION_EVIDENCE_STATES.some(state => state === value);
}

export function isSessionObservabilityReasonCode(
  value: unknown,
): value is SessionObservabilityReasonCode {
  return SESSION_OBSERVABILITY_REASON_CODES.some(code => code === value);
}

export function isSessionObservabilityActionCode(
  value: unknown,
): value is SessionObservabilityActionCode {
  return SESSION_OBSERVABILITY_ACTION_CODES.some(code => code === value);
}

export function isSessionObservabilityProjection(
  value: unknown,
): value is SessionObservabilityProjection {
  if (!isRecord(value) || !hasOnlyKeys(value, OBSERVABILITY_PROJECTION_KEYS)) {
    return false;
  }
  const capabilities = value.capabilities;
  if (
    (value.runtimeKind !== 'claude' && value.runtimeKind !== 'codex') ||
    !isSessionIntegrationState(value.state) ||
    !isSessionObservabilityActionCode(value.actionCode) ||
    !validTimestamp(value.observedAt) ||
    !Array.isArray(capabilities) ||
    capabilities.length !== SESSION_OBSERVABILITY_CAPABILITIES.length ||
    (value.skillShortcuts !== undefined &&
      !isSkillShortcutEvidence(value.skillShortcuts)) ||
    !capabilities.every(isSessionCapabilityEvidence)
  ) {
    return false;
  }
  const observedCapabilities = new Set(
    capabilities.map(evidence => evidence.capability),
  );
  if (
    !SESSION_OBSERVABILITY_CAPABILITIES.every(capability =>
      observedCapabilities.has(capability),
    )
  ) {
    return false;
  }
  return validSessionStateAndAction({
    runtimeKind: value.runtimeKind,
    state: value.state,
    capabilities,
    actionCode: value.actionCode,
    observedAt: value.observedAt,
  });
}

function isSessionCapabilityEvidence(
  value: unknown,
): value is SessionCapabilityEvidence {
  if (!isRecord(value) || !hasOnlyKeys(value, CAPABILITY_EVIDENCE_KEYS)) {
    return false;
  }
  if (
    !isSessionObservabilityCapability(value.capability) ||
    !isSessionEvidenceState(value.state)
  ) {
    return false;
  }
  if (value.state === 'confirmed') {
    return value.reasonCode === null && validTimestamp(value.observedAt);
  }
  if (value.state === 'pending') {
    return (
      value.reasonCode === null &&
      (value.observedAt === null || validTimestamp(value.observedAt))
    );
  }
  return (
    isSessionObservabilityReasonCode(value.reasonCode) &&
    (value.observedAt === null || validTimestamp(value.observedAt))
  );
}

function validSessionStateAndAction(
  value: SessionObservabilityProjection,
): boolean {
  if (value.state === 'integrated') {
    return (
      value.actionCode === 'none' &&
      value.capabilities.every(evidence =>
        ['confirmed', 'pending'].includes(evidence.state),
      ) &&
      ['binding', 'mcp', 'skill-vault'].every(capability =>
        value.capabilities.some(
          evidence =>
            evidence.capability === capability &&
            evidence.state === 'confirmed',
        ),
      )
    );
  }
  if (value.state === 'degraded') {
    return (
      ['retry', 'reconnect', 'upgrade-runtime'].includes(value.actionCode) &&
      value.capabilities.every(evidence => evidence.state !== 'rejected') &&
      value.capabilities.some(evidence =>
        ['unavailable', 'unsupported'].includes(evidence.state),
      )
    );
  }
  return (
    ['reconnect', 'repair', 'upgrade-runtime'].includes(value.actionCode) &&
    value.capabilities.some(evidence => evidence.state === 'rejected')
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  return Object.keys(value).every(key => keys.includes(key));
}

function validTimestamp(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}
